import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const QUOTE_URL = '/quote/sports?ref=leagueapps';

const LA_CSS = `
.la-page {
  --grass: #187D1D;
  --forrest: #005000;
  --lime: #6ECA3F;
  --sky: #80C9EC;
  --sun: #EED41B;
  --ink: #111111;
  --paper: #FFFFFF;
  --mist: #F4F7F4;
  --link: #1255CC;
  --display: 'Lexend Deca', sans-serif;
  --body: 'Assistant', sans-serif;
  font-family: var(--body);
  color: var(--ink);
  background: var(--paper);
  line-height: 1.6;
  scroll-behavior: smooth;
}
.la-page * { margin: 0; padding: 0; box-sizing: border-box; }
.la-page img { max-width: 100%; display: block; }
.la-page a { color: var(--link); font-weight: 700; text-decoration: underline; }
.la-page .wrap { max-width: 1140px; margin: 0 auto; padding: 0 28px; }
.la-page h1, .la-page h2, .la-page h3, .la-page .btn { font-family: var(--display); }
/* ============ TOP BAR ============ */
.la-page .topbar { background: var(--paper); border-bottom: 1px solid #E3E8E3; position: sticky; top: 0; z-index: 50; }
.la-page .topbar-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; }
.la-page .cobrand { display: flex; align-items: center; gap: 16px; }
.la-page .cobrand img.la { height: 34px; width: auto; }
.la-page .cobrand img.ph { height: 26px; width: auto; }
.la-page .cobrand .x { font-family: var(--display); font-weight: 600; font-size: 16px; color: #9AA59A; }
.la-page .btn {
  display: inline-block; font-weight: 700; font-size: 16px;
  padding: 13px 28px; border-radius: 6px; text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  cursor: pointer; border: none;
}
.la-page .btn:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,.18); }
.la-page .btn-grass { background: var(--grass); color: #fff; }
.la-page .btn-grass:hover { background: var(--forrest); }
.la-page .btn-sun { background: var(--sun); color: var(--ink); }
.la-page .btn-sun:hover { background: #F7E13A; }
.la-page .btn-ghost { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,.65); }
.la-page .btn-ghost:hover { border-color: #fff; box-shadow: none; }
.la-page .topbar .btn { padding: 10px 22px; font-size: 15px; }
/* ============ HERO ============ */
.la-page .hero { background: var(--grass); color: #fff; position: relative; overflow: hidden; }
.la-page .hero::before {
  content: ''; position: absolute; inset: 0;
  background: repeating-linear-gradient(115deg,
    rgba(255,255,255,0) 0px, rgba(255,255,255,0) 180px,
    rgba(255,255,255,.05) 180px, rgba(255,255,255,.05) 230px);
  pointer-events: none;
}
.la-page .hero-mark { position: absolute; right: -60px; bottom: -80px; width: 480px; opacity: .10; transform: rotate(-6deg); pointer-events: none; }
.la-page .hero-inner { position: relative; padding: 96px 0 110px; max-width: 720px; }
.la-page .eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-family: var(--display); font-weight: 600; font-size: 13px;
  letter-spacing: .14em; text-transform: uppercase; color: var(--sun); margin-bottom: 26px;
}
.la-page .eyebrow::before { content: ''; width: 34px; height: 4px; background: var(--sun); transform: skewX(-25deg); }
.la-page .hero h1 { font-weight: 800; font-size: clamp(38px, 5.4vw, 62px); line-height: 1.08; letter-spacing: -.01em; margin-bottom: 24px; }
.la-page .hero h1 em { font-style: normal; color: var(--sun); }
.la-page .hero p.lede { font-size: 19px; max-width: 560px; color: rgba(255,255,255,.92); margin-bottom: 38px; }
.la-page .hero-ctas { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
.la-page .hero-note { margin-top: 22px; font-size: 14px; color: rgba(255,255,255,.75); }
.la-page .hero-edge { position: absolute; bottom: 0; left: 0; right: 0; height: 64px; background: var(--paper); clip-path: polygon(0 100%, 100% 100%, 100% 0); }
/* ============ TRUST STRIP ============ */
.la-page .trust { padding: 54px 0 10px; }
.la-page .trust-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
.la-page .stat { border-left: 5px solid var(--lime); padding-left: 20px; }
.la-page .stat .num { font-family: var(--display); font-weight: 800; font-size: 40px; color: var(--grass); line-height: 1; }
.la-page .stat .lbl { font-size: 15px; color: #3A463A; margin-top: 6px; }
/* ============ SECTIONS ============ */
.la-page section { padding: 84px 0; }
.la-page .sec-head { max-width: 640px; margin-bottom: 48px; }
.la-page .kicker {
  font-family: var(--display); font-weight: 600; font-size: 13px;
  letter-spacing: .14em; text-transform: uppercase; color: var(--grass);
  display: inline-flex; align-items: center; gap: 10px; margin-bottom: 14px;
}
.la-page .kicker::before { content: ''; width: 28px; height: 4px; background: var(--grass); transform: skewX(-25deg); }
.la-page h2 { font-weight: 800; font-size: clamp(28px, 3.6vw, 42px); line-height: 1.12; color: var(--ink); }
.la-page .sec-head p { margin-top: 16px; font-size: 17px; color: #3A463A; }
/* ============ HOW IT WORKS ============ */
.la-page .steps { background: var(--mist); }
.la-page .step-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
.la-page .step { background: #fff; border-radius: 10px; padding: 34px 30px; border-top: 6px solid var(--grass); box-shadow: 0 2px 10px rgba(0,80,0,.06); }
.la-page .step .n { font-family: var(--display); font-weight: 800; font-size: 15px; color: var(--grass); letter-spacing: .1em; margin-bottom: 14px; }
.la-page .step h3 { font-weight: 700; font-size: 21px; margin-bottom: 10px; }
.la-page .step p { font-size: 15.5px; color: #3A463A; }
.la-page .step .time {
  display: inline-block; margin-top: 16px; font-family: var(--display);
  font-weight: 600; font-size: 12.5px; letter-spacing: .08em; text-transform: uppercase;
  background: #E9F5E2; color: var(--forrest); padding: 5px 12px; border-radius: 4px;
}
/* ============ COVERAGE ============ */
.la-page .cov-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; }
.la-page .cov { border: 1.5px solid #DCE5DC; border-radius: 10px; padding: 30px 28px; transition: border-color .15s ease, box-shadow .15s ease; position: relative; overflow: hidden; }
.la-page .cov:hover { border-color: var(--lime); box-shadow: 0 6px 20px rgba(24,125,29,.10); }
.la-page .cov::after { content: ''; position: absolute; top: 0; right: 0; width: 54px; height: 8px; background: var(--lime); transform: skewX(-25deg) translateX(8px); }
.la-page .cov h3 { font-weight: 700; font-size: 19px; margin-bottom: 6px; }
.la-page .cov .limit { font-family: var(--display); font-weight: 800; font-size: 24px; color: var(--grass); margin-bottom: 12px; }
.la-page .cov p { font-size: 15px; color: #3A463A; }
/* ============ SAFETY PLATFORM ============ */
.la-page .safety { background: var(--forrest); color: #fff; position: relative; overflow: hidden; }
.la-page .safety::before {
  content: ''; position: absolute; inset: 0;
  background: repeating-linear-gradient(115deg,
    rgba(255,255,255,0) 0px, rgba(255,255,255,0) 220px,
    rgba(255,255,255,.04) 220px, rgba(255,255,255,.04) 270px);
  pointer-events: none;
}
.la-page .safety .wrap { position: relative; }
.la-page .safety .kicker { color: var(--sun); }
.la-page .safety .kicker::before { background: var(--sun); }
.la-page .safety h2 { color: #fff; }
.la-page .safety .sec-head p { color: rgba(255,255,255,.88); }
.la-page .safety-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px 36px; }
.la-page .safety-item { display: flex; gap: 14px; align-items: flex-start; }
.la-page .safety-item .tick { flex: 0 0 auto; width: 26px; height: 26px; border-radius: 50%; background: var(--lime); color: var(--forrest); display: grid; place-items: center; font-weight: 800; font-size: 15px; margin-top: 2px; }
.la-page .safety-item h3 { font-weight: 700; font-size: 17.5px; margin-bottom: 4px; }
.la-page .safety-item p { font-size: 14.5px; color: rgba(255,255,255,.85); }
.la-page .safety .price-note { margin-top: 44px; padding: 22px 28px; border-left: 5px solid var(--sun); background: rgba(255,255,255,.06); border-radius: 0 8px 8px 0; font-size: 16.5px; max-width: 680px; }
.la-page .safety .price-note strong { color: var(--sun); }
/* ============ PARTNERSHIP ============ */
.la-page .partner-flex { display: grid; grid-template-columns: 1.1fr .9fr; gap: 60px; align-items: center; }
.la-page .partner-logos { background: var(--mist); border-radius: 12px; padding: 48px 40px; display: flex; flex-direction: column; align-items: center; gap: 26px; }
.la-page .partner-logos img.la { height: 44px; width: auto; }
.la-page .partner-logos img.ph { height: 36px; width: auto; }
.la-page .partner-logos .plus { font-family: var(--display); font-weight: 800; font-size: 22px; color: var(--grass); }
.la-page .partner-copy p { font-size: 16.5px; color: #2C362C; margin-bottom: 16px; }
.la-page .partner-copy p:last-child { margin-bottom: 0; }
/* ============ FAQ ============ */
.la-page .faq { background: var(--mist); }
.la-page .faq-list { max-width: 780px; }
.la-page details { background: #fff; border-radius: 8px; margin-bottom: 14px; border: 1.5px solid #DCE5DC; overflow: hidden; }
.la-page details[open] { border-color: var(--lime); }
.la-page summary { cursor: pointer; list-style: none; padding: 20px 24px; font-family: var(--display); font-weight: 700; font-size: 17px; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.la-page summary::-webkit-details-marker { display: none; }
.la-page summary::after { content: '+'; font-size: 24px; color: var(--grass); font-weight: 600; transition: transform .2s ease; }
.la-page details[open] summary::after { transform: rotate(45deg); }
.la-page details .ans { padding: 0 24px 22px; font-size: 15.5px; color: #3A463A; }
/* ============ FINAL CTA ============ */
.la-page .final { background: var(--grass); color: #fff; text-align: center; position: relative; overflow: hidden; }
.la-page .final::before {
  content: ''; position: absolute; inset: 0;
  background: repeating-linear-gradient(115deg,
    rgba(255,255,255,0) 0px, rgba(255,255,255,0) 180px,
    rgba(255,255,255,.05) 180px, rgba(255,255,255,.05) 230px);
  pointer-events: none;
}
.la-page .final .wrap { position: relative; }
.la-page .final h2 { color: #fff; font-size: clamp(32px, 4.4vw, 52px); margin-bottom: 14px; }
.la-page .final h2 em { font-style: normal; color: var(--sun); }
.la-page .final p { font-size: 18px; color: rgba(255,255,255,.9); max-width: 560px; margin: 0 auto 36px; }
.la-page .final .hero-ctas { justify-content: center; }
/* ============ FOOTER ============ */
.la-page footer { background: var(--ink); color: #B9C2B9; padding: 44px 0; font-size: 14px; }
.la-page .foot-inner { display: flex; justify-content: space-between; align-items: center; gap: 24px; flex-wrap: wrap; }
.la-page .foot-logos { display: flex; align-items: center; gap: 18px; }
.la-page .foot-logos img.la { height: 26px; width: auto; }
.la-page .foot-logos img.ph { height: 22px; width: auto; }
.la-page .foot-logos .x { color: #5A655A; font-family: var(--display); }
.la-page footer a { color: var(--sky); }
.la-page .disclaim { margin-top: 22px; font-size: 12.5px; color: #7A857A; max-width: 880px; line-height: 1.55; }
/* ============ RESPONSIVE ============ */
@media (max-width: 900px) {
  .la-page .trust-grid, .la-page .step-grid, .la-page .cov-grid, .la-page .safety-grid { grid-template-columns: 1fr 1fr; }
  .la-page .partner-flex { grid-template-columns: 1fr; gap: 36px; }
}
@media (max-width: 600px) {
  .la-page .trust-grid, .la-page .step-grid, .la-page .cov-grid, .la-page .safety-grid { grid-template-columns: 1fr; }
  .la-page .hero-inner { padding: 64px 0 90px; }
  .la-page .hero-mark { width: 320px; right: -100px; }
  .la-page section { padding: 60px 0; }
  .la-page .topbar .btn { display: none; }
  .la-page .cobrand img.la { height: 28px; }
  .la-page .cobrand img.ph { height: 21px; }
}
@media (prefers-reduced-motion: reduce) {
  .la-page { scroll-behavior: auto; }
  .la-page .btn { transition: none; }
}
.la-page .btn:focus-visible, .la-page summary:focus-visible, .la-page a:focus-visible { outline: 3px solid var(--sky); outline-offset: 2px; }
`;

const FAQ = [
  {
    q: 'Who is eligible for this program?',
    a: "Youth and amateur sports organizations — clubs, leagues, camps, tournaments, and training programs. You don't need to be a LeagueApps customer to request a quote, though the program is built around the needs of organizations like the ones that run on LeagueApps.",
  },
  {
    q: 'How fast can I get a certificate of insurance?',
    a: 'Once your policy is bound, certificates are available instantly online — including additional insured certificates for facilities, municipalities, and sanctioning bodies that require them.',
  },
  {
    q: 'What does the quote cost?',
    a: "Nothing. The quote is free and carries no obligation. Your premium depends on your sport, participant count, and coverage selections — you'll see exact numbers before you decide anything.",
  },
  {
    q: 'Is the safety platform really included?',
    a: "Yes. Every policy includes the full Players Health athlete safety platform at no added cost — policy templates and your Athlete Safety Score. It's a core part of how the program works, not an upsell.",
  },
  {
    q: 'What if I have coverage already?',
    a: "It doesn't hurt to get another quote to see if there are savings.",
  },
];

const COVERAGE = [
  { h: 'General Liability', limit: '$1M / $3M', p: 'Per-occurrence and aggregate protection for bodily injury and property damage claims — the coverage your facilities and sanctioning bodies require.' },
  { h: 'Participant Accident', limit: '$25K per athlete', p: 'Medical expense coverage for injuries your athletes sustain in practices, games, and sanctioned events.' },
  { h: 'Equipment', limit: 'Property coverage', p: 'Protection for the goals, mats, uniforms, and gear your program owns — at your facility or on the road.' },
  { h: 'Tournaments & Events', limit: 'Event coverage', p: 'Short-term coverage for the tournaments, camps, clinics, and showcases you host throughout the year.' },
];

const SAFETY = [
  { h: 'Safety policies & templates', p: 'Ready-to-adopt policy libraries — emergency action plans, return-to-play, communication guidelines.' },
  { h: 'Athlete Safety Score', p: "A measurable view of your organization's risk posture — and a track record that strengthens your renewal." },
];

const LeagueAppsLandingPage = () => {
  return (
    <div className="la-page" data-testid="leagueapps-landing-page">
      <Helmet>
        <title>LeagueApps + Players Health — Insurance for Organizations That Make Sports Happen</title>
        <meta name="description" content="Purpose-built insurance for the clubs, camps, tournaments, and leagues that make sports happen — quoted in 24 hours, bound online, with an athlete safety platform included at no extra cost." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@500;600;700;800&family=Assistant:wght@400;600;700&display=swap" rel="stylesheet" />
        <meta property="og:title" content="LeagueApps + Players Health — Insurance for Sports Organizations" />
        <meta property="og:description" content="Coverage built for youth and amateur sports. Quoted in 24 hours, bound online, safety platform included." />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{LA_CSS}</style>

      {/* ============ TOP BAR ============ */}
      <div className="topbar">
        <div className="wrap topbar-inner">
          <div className="cobrand">
            <img className="la" src="/leagueapps-logo-0.png" alt="LeagueApps" />
            <span className="x">{'\u00d7'}</span>
            <img className="ph" src="/leagueapps-logo-1.png" alt="Players Health" />
          </div>
          <Link className="btn btn-grass" to={QUOTE_URL} data-testid="topbar-quote-cta">Start My Quote</Link>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <header className="hero">
        <img className="hero-mark" src="/leagueapps-logo-2.png" alt="" aria-hidden="true" />
        <div className="wrap">
          <div className="hero-inner">
            <span className="eyebrow">A LeagueApps + Players Health Partnership</span>
            <h1>Insurance for the organizations that <em>make sports happen.</em></h1>
            <p className="lede">Purpose-built coverage for the clubs, camps, tournaments, and leagues that run on LeagueApps — quoted in 24 hours, bound online, with an athlete safety platform included at no extra cost.</p>
            <div className="hero-ctas">
              <Link className="btn btn-sun" to={QUOTE_URL} data-testid="hero-quote-cta">Start My Quote</Link>
              <a className="btn btn-ghost" href="#coverage" data-testid="hero-see-covered">See What's Covered</a>
            </div>
            <p className="hero-note">Takes about 5 minutes. No obligation to bind.</p>
          </div>
        </div>
        <div className="hero-edge" aria-hidden="true"></div>
      </header>

      {/* ============ TRUST STRIP ============ */}
      <div className="trust">
        <div className="wrap trust-grid">
          <div className="stat"><div className="num">24 hrs</div><div className="lbl">From quote request to a bindable quote in your inbox</div></div>
          <div className="stat"><div className="num">1 link</div><div className="lbl">Quote, bind, pay, and pull certificates of insurance — all online</div></div>
          <div className="stat"><div className="num">$0</div><div className="lbl">Added cost for the Players Health athlete safety platform</div></div>
        </div>
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <section className="steps" id="how">
        <div className="wrap">
          <div className="sec-head">
            <span className="kicker">How It Works</span>
            <h2>From kickoff to covered in three steps.</h2>
            <p>You already run registration, payments, and scheduling on LeagueApps. Now your insurance works the same way — fast, online, and built for sports.</p>
          </div>
          <div className="step-grid">
            <div className="step">
              <div className="n">STEP 1</div>
              <h3>Tell us about your organization</h3>
              <p>Sport, headcount, season dates, venues. The questions are written for sports organizations — not generic small businesses.</p>
              <span className="time">~5 minutes</span>
            </div>
            <div className="step">
              <div className="n">STEP 2</div>
              <h3>Get your quote</h3>
              <p>A sports-specialist underwriting team reviews your submission and returns a bindable quote — not a ballpark estimate.</p>
              <span className="time">Within 24 hours</span>
            </div>
            <div className="step">
              <div className="n">STEP 3</div>
              <h3>Bind and play</h3>
              <p>Accept online, pay online, and download certificates of insurance instantly — including additional insureds for your facilities.</p>
              <span className="time">Same day</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COVERAGE ============ */}
      <section id="coverage">
        <div className="wrap">
          <div className="sec-head">
            <span className="kicker">The Coverage Stack</span>
            <h2>Built for how youth and amateur sports actually operate.</h2>
            <p>One program, written by underwriters who only do sports. Every policy is placed through Players Health, a licensed managing general agency specializing in youth and amateur athletics.</p>
          </div>
          <div className="cov-grid">
            {COVERAGE.map((c) => (
              <div className="cov" key={c.h}>
                <h3>{c.h}</h3>
                <div className="limit">{c.limit}</div>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SAFETY PLATFORM ============ */}
      <section className="safety">
        <div className="wrap">
          <div className="sec-head">
            <span className="kicker">Included With Every Policy</span>
            <h2>The athlete safety platform comes with it. Free.</h2>
            <p>Players Health was founded on a simple idea: the safest environment for an athlete is also the best risk. Every insured organization gets the full safety platform at no added cost.</p>
          </div>
          <div className="safety-grid">
            {SAFETY.map((s) => (
              <div className="safety-item" key={s.h}>
                <span className="tick">{'\u2713'}</span>
                <div><h3>{s.h}</h3><p>{s.p}</p></div>
              </div>
            ))}
          </div>
          <div className="price-note">
            <strong>Why free?</strong> Safer organizations have fewer and less severe claims. When you invest in safety, your insurance should reward it — that's the whole model.
          </div>
        </div>
      </section>

      {/* ============ PARTNERSHIP ============ */}
      <section>
        <div className="wrap partner-flex">
          <div className="partner-copy">
            <div className="sec-head" style={{ marginBottom: '24px' }}>
              <span className="kicker">Why This Partnership</span>
              <h2>Two platforms. One job: keep your athletes playing.</h2>
            </div>
            <p>LeagueApps powers the operations of youth and amateur sports — registration, payments, scheduling, and the tools that let organizers focus on the game. Players Health protects what those organizers build, insuring youth and amateur sports organizations across the country.</p>
            <p>Together, we're closing the gap between running a great program and protecting one. Your LeagueApps account isn't required to get a quote — but if you run on LeagueApps, this program was designed with you in mind.</p>
            <p>Questions before you start? <a href="mailto:quotes@playershealth.com">Talk to a Players Health advisor</a>.</p>
          </div>
          <div className="partner-logos">
            <img className="la" src="/leagueapps-logo-3.png" alt="LeagueApps" />
            <span className="plus">+</span>
            <img className="ph" src="/leagueapps-logo-4.png" alt="Players Health" />
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="faq">
        <div className="wrap">
          <div className="sec-head">
            <span className="kicker">Good Questions</span>
            <h2>Before you ask.</h2>
          </div>
          <div className="faq-list">
            {FAQ.map((item, i) => (
              <details key={i} data-testid={`faq-item-${i}`}>
                <summary>{item.q}</summary>
                <div className="ans">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="final">
        <div className="wrap">
          <h2>Make sports happen. <em>Play forever.</em></h2>
          <p>Five minutes of questions. A real quote in 24 hours. Coverage and a safety platform built for the way you run sports.</p>
          <div className="hero-ctas">
            <Link className="btn btn-sun" to={QUOTE_URL} data-testid="final-quote-cta">Start My Quote</Link>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="wrap">
          <div className="foot-inner">
            <div className="foot-logos">
              <img className="la" src="/leagueapps-logo-5.png" alt="LeagueApps" />
              <span className="x">{'\u00d7'}</span>
              <img className="ph" src="/leagueapps-logo-6.png" alt="Players Health" />
            </div>
            <div>
              <a href="/leagueapps" data-testid="footer-link-leagueapps">phquote.com/leagueapps</a> &nbsp;·&nbsp; <a href="mailto:quotes@playershealth.com">quotes@playershealth.com</a>
            </div>
          </div>
          <p className="disclaim">Insurance products are offered through Players Health, a licensed insurance producer. Coverage descriptions on this page are summaries only; actual terms, conditions, limits, and exclusions are governed by the issued policy. Coverage availability may vary by state. LeagueApps is a technology partner and is not a licensed insurance producer.</p>
        </div>
      </footer>
    </div>
  );
};

export default LeagueAppsLandingPage;
